import { Close } from '@carbon/icons-react';
import { type PartialTheme, ThemeProvider, useTheme } from '@repo/theme';
import { BreadCrumb } from 'component/button';
import { useStory } from 'data/stories';
import { type ArticleTeaserConfig, QuestionVideoOverlayPromo } from 'feature/teaser/editorial-question';
import { useVideo } from 'open-modules/zdf/component/videoplayer/videoplayer-state';
import { updateVideoVotingSessionData } from 'open-modules/zdf/videovoting/videoVotingSession';
import React, { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { ConfigContext, useDatastore, useInstanceKey } from 'util/datastore';
import { useConfig, useInstanceFeatures, useScreenSet } from 'util/features-hooks';
import { t } from 'util/stdtypes';

import { VideoVotingOverlay } from './videovoting-overlay';
import { useVideoVoting } from './videovoting-util';

export function VideoVotingEndScreen() {
    const videovoting = useVideoVoting();
    const windowDimensions = useWindowDimensions();

    // Show the teaser only at the end of the video
    const video = useVideo();
    const shouldShowTeaser = video && video?.duration - video?.currentTime < 60;

    const linkedSpaceKey = useLinkedSpaceKeyOnThisStory(videovoting?.linkedSpaceKey);

    const config = useConfig<ArticleTeaserConfig>();
    const screenSet = useScreenSet();
    const activeFeatures = useInstanceFeatures();
    const overlayTheme = useVideoOverlayTeaserTheme();
    const newConfig: ArticleTeaserConfig = useMemo(
        () => ({
            ...config,
            teaserQuestionLabel: t('video_voting.join_discussion', 'Join the discussion'),
            promoTopRightWidgets: [VideoCommentPreviewTeaserCloseButton],
            additionalAdminTeaserWidgets: [],
        }),
        [config],
    );

    const configContextValue = useMemo(() => ({ config: newConfig, screenSet, activeFeatures }), [newConfig, screenSet, activeFeatures]);

    return (
        <VideoVotingOverlay
            pointerEvents={shouldShowTeaser && !!linkedSpaceKey}
            style={{
                width: '80%',
                minWidth: Math.min(525, windowDimensions.width * 0.9),
                maxWidth: 720,
                top: '5%',
            }}
            visible={shouldShowTeaser}
        >
            <ThemeProvider theme={overlayTheme}>
                <ConfigContext.Provider value={configContextValue}>
                    {linkedSpaceKey ? <QuestionVideoOverlayPromo spaceKey={linkedSpaceKey} /> : null}
                </ConfigContext.Provider>
            </ThemeProvider>
        </VideoVotingOverlay>
    );
}

/**
 * Repaints the teaser's entry-point tokens as a translucent dark panel, so the
 * promo stays legible over the playing video.
 */
function useVideoOverlayTeaserTheme(): PartialTheme {
    const { colors } = useTheme();
    return useMemo(() => {
        const translucentPanel = colors.colorTextOnTagLight + 'CC';
        return {
            colors: {
                colorEntryPointColorGreen: translucentPanel,
                colorEntryPointColorGreenTint: translucentPanel,
                colorTextOnEntryPointColorBg: colors.colorTextOnAccentColorBg,
            },
        };
    }, [colors]);
}

/**
 * The voting's linked space, but only when the story this overlay renders on
 * lists it. A voting assigned to one story can name a space that story does not
 * connect to, and the teaser must not promote a discussion the page never
 * linked.
 */
function useLinkedSpaceKeyOnThisStory(linkedSpaceKey?: string | null): string | null {
    const storyKey = useInstanceKey();
    const spaceKeys = useStory(storyKey)?.spaceKeys ?? [];
    if (!linkedSpaceKey || !spaceKeys.includes(linkedSpaceKey)) return null;
    return linkedSpaceKey;
}

/** @public */
export function VideoCommentPreviewTeaserCloseButton() {
    const datastore = useDatastore();
    const { colors } = useTheme();
    const closeWindow = () => {
        updateVideoVotingSessionData(datastore, { teaserDismissed: true });
    };
    return <BreadCrumb icon={Close} iconProps={{ size: 32, color: colors.colorTextOnAccentColorBg }} onPress={closeWindow} />;
}
